import { useOrganization } from '@clerk/clerk-react'
import { syncOrganizationUpdate } from '@saaga/shared/store/slices/auth.slice'
import { useAppDispatch } from '@saaga/shared/store/store'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { APP_BASE_PATH } from '@/app.const'
import {
  generateSlugSuggestions,
  isClerkSlugConflict,
  SlugConflictError,
} from '@/components/org-settings/org-slug.util'
import { transformOrganization } from '@/hooks/use-auth-sync.helper'
import { orgRoutes, settingsRoutes } from '@/routes/org-routes.const'

export interface UseGeneralSettingsReturn {
  name: string
  slug: string
  imageUrl: string | null
  isAdmin: boolean
  isNameLoading: boolean
  isSlugLoading: boolean
  isLogoLoading: boolean
  handleNameChange: (name: string) => Promise<void>
  handleSlugChange: (slug: string) => Promise<void>
  handleLogoChange: (file: File | null) => Promise<void>
  handleLogoRemove: () => Promise<void>
}

export const useGeneralSettings = (): UseGeneralSettingsReturn => {
  const { organization, membership } = useOrganization()
  const dispatch = useAppDispatch()

  const syncOrgToRedux = useCallback(
    (org: NonNullable<typeof organization>) => {
      dispatch(syncOrganizationUpdate(transformOrganization(org)))
    },
    [dispatch]
  )

  const [isNameLoading, setIsNameLoading] = useState(false)
  const [isSlugLoading, setIsSlugLoading] = useState(false)
  const [isLogoLoading, setIsLogoLoading] = useState(false)

  const handleNameChange = useOrgNameHandler({
    organization,
    syncOrgToRedux,
    setIsNameLoading,
  })

  const handleSlugChange = useOrgSlugHandler({
    organization,
    setIsSlugLoading,
  })

  const { handleLogoChange, handleLogoRemove } = useOrgLogoHandlers({
    organization,
    syncOrgToRedux,
    setIsLogoLoading,
  })

  return {
    name: organization?.name ?? '',
    slug: organization?.slug ?? '',
    imageUrl: organization?.imageUrl ?? null,
    isAdmin: membership?.role === 'org:admin',
    isNameLoading,
    isSlugLoading,
    isLogoLoading,
    handleNameChange,
    handleSlugChange,
    handleLogoChange,
    handleLogoRemove,
  }
}

type OrgResource = NonNullable<ReturnType<typeof useOrganization>['organization']>

interface OrgHandlerBaseParams {
  organization: ReturnType<typeof useOrganization>['organization']
  syncOrgToRedux: (org: OrgResource) => void
}

interface OrgNameHandlerParams extends OrgHandlerBaseParams {
  setIsNameLoading: (loading: boolean) => void
}

const useOrgNameHandler = ({
  organization,
  syncOrgToRedux,
  setIsNameLoading,
}: OrgNameHandlerParams): ((name: string) => Promise<void>) =>
  useCallback(
    async (name: string): Promise<void> => {
      if (!organization) return
      setIsNameLoading(true)
      try {
        const updatedOrg = await organization.update({ name })
        syncOrgToRedux(updatedOrg)
        toast.success('Organization name updated successfully')
      } catch (error) {
        logger.error('Failed to update organization name:', error)
        toast.error('Failed to update organization name')
      } finally {
        setIsNameLoading(false)
      }
    },
    [organization, syncOrgToRedux, setIsNameLoading]
  )

interface OrgSlugHandlerParams {
  organization: ReturnType<typeof useOrganization>['organization']
  setIsSlugLoading: (loading: boolean) => void
}

const useOrgSlugHandler = ({
  organization,
  setIsSlugLoading,
}: OrgSlugHandlerParams): ((slug: string) => Promise<void>) =>
  useCallback(
    async (slug: string): Promise<void> => {
      if (!organization) return
      setIsSlugLoading(true)
      try {
        const updatedOrg = await organization.update({ name: organization.name, slug })
        const confirmedSlug = updatedOrg.slug
        if (!confirmedSlug) {
          throw new Error('Clerk did not return a valid slug')
        }
        toast.success('Organization URL updated. Redirecting...')
        window.location.href = `${APP_BASE_PATH}/${confirmedSlug}/${orgRoutes.settings}/${settingsRoutes.general}`
      } catch (error) {
        if (isClerkSlugConflict(error)) {
          setIsSlugLoading(false)
          throw new SlugConflictError(slug, generateSlugSuggestions(slug))
        }
        logger.error('Failed to update organization slug:', error)
        toast.error('Failed to update organization URL.')
        setIsSlugLoading(false)
      }
    },
    [organization, setIsSlugLoading]
  )

interface OrgLogoHandlersParams extends OrgHandlerBaseParams {
  setIsLogoLoading: (loading: boolean) => void
}

const useOrgLogoHandlers = ({
  organization,
  syncOrgToRedux,
  setIsLogoLoading,
}: OrgLogoHandlersParams): {
  handleLogoChange: (file: File | null) => Promise<void>
  handleLogoRemove: () => Promise<void>
} => {
  const handleLogoChange = useCallback(
    async (file: File | null): Promise<void> => {
      if (!organization || !file) return
      setIsLogoLoading(true)
      try {
        const updatedOrg = await organization.setLogo({ file })
        syncOrgToRedux(updatedOrg)
        toast.success('Organization logo updated successfully')
      } catch (error) {
        logger.error('Failed to update organization logo:', error)
        toast.error('Failed to update organization logo')
      } finally {
        setIsLogoLoading(false)
      }
    },
    [organization, syncOrgToRedux, setIsLogoLoading]
  )

  const handleLogoRemove = useCallback(async (): Promise<void> => {
    if (!organization) return
    setIsLogoLoading(true)
    try {
      const updatedOrg = await organization.setLogo({ file: null })
      syncOrgToRedux(updatedOrg)
      toast.success('Organization logo removed successfully')
    } catch (error) {
      logger.error('Failed to remove organization logo:', error)
      toast.error('Failed to remove organization logo')
    } finally {
      setIsLogoLoading(false)
    }
  }, [organization, syncOrgToRedux, setIsLogoLoading])

  return { handleLogoChange, handleLogoRemove }
}
